import { Injectable, inject } from '@angular/core';
import { EPatientTitle, ETenantProfile } from '@insolution/graphql-data-access';
import { MqttService } from '@insolution/mqtt-data-access';
import { CurrentTenantService } from '@insolution/tenant-data-access';
import type { MqttClient } from 'mqtt';
import { EImportErrorCode } from '../enums/import-error-code.enum';
import { ImportPatientError } from '../errors/import-patient.error';
import { IImportPatientData } from '../interfaces/import-patient-data.interface';

const IMPORT_PATIENT_CONNECT_TIMEOUT_MS = 5_000;
const IMPORT_PATIENT_RESPONSE_TIMEOUT_MS = 10_000;
const IMPORT_REQUEST_TOPIC = 'imcl/patient-import/request';

const PODONET_COUNTRY_CODE_ALIASES: Record<string, string> = {
  NED: 'NLD',
};

const POSTAL_CODE_COUNTRY_PATTERNS: { pattern: RegExp; countryCode: string }[] = [
  { pattern: /^\d{4}\s?[A-Za-z]{2}$/, countryCode: 'NLD' },
];

type LeilaPatientImportRequest = {
  requestId: string;
  tenantProfile: ETenantProfile;
};

type LeilaImportedPatientPayload = Pick<
  IImportPatientData,
  | 'external_id'
  | 'title'
  | 'last_name'
  | 'middle_name'
  | 'first_name'
  | 'date_of_birth'
  | 'street'
  | 'house_number'
  | 'city'
  | 'postal_code'
  | 'country'
  | 'phone'
  | 'mobile'
  | 'email'
>;

type LeilaPatientImportSuccessResponse = {
  type: 'patient_import_result';
  requestId: string;
  ok: true;
  patient: LeilaImportedPatientPayload;
};

type LeilaPatientImportErrorResponse = {
  type: 'patient_import_result';
  requestId: string;
  ok: false;
  code?: string;
  message?: string;
};

type LeilaPatientImportResponse = LeilaPatientImportSuccessResponse | LeilaPatientImportErrorResponse;

@Injectable({
  providedIn: 'root',
})
export class ImportPatientService {
  private readonly mqttService = inject(MqttService);
  private readonly currentTenantService = inject(CurrentTenantService);

  public async importPatient(): Promise<IImportPatientData> {
    const tenantProfile = this.currentTenantService.currentTenantProfile();
    if (!tenantProfile) {
      throw this.createNoDataFetchedError('No tenant profile selected for patient import.');
    }

    const requestId = crypto.randomUUID();
    const responseTopic = `imcl/patient-import/response/${requestId}`;
    const requestPayload: LeilaPatientImportRequest = {
      requestId,
      tenantProfile,
    };

    let mqttClient: MqttClient | null = null;
    let onMessageHandler: ((topic: string, payload: string | Uint8Array | ArrayBuffer) => void) | null = null;

    try {
      mqttClient = await this.connectWithTimeout();
      await this.mqttService.subscribeToTopics(mqttClient, [responseTopic], 1);

      const activeMqttClient = mqttClient;

      return await new Promise<IImportPatientData>((resolve, reject) => {
        const responseTimer = window.setTimeout(
          () => reject(this.createNoDataFetchedError('Timed out while waiting for patient import response.')),
          IMPORT_PATIENT_RESPONSE_TIMEOUT_MS
        );

        onMessageHandler = (topic, payload): void => {
          if (topic !== responseTopic) {
            return;
          }

          const payloadText = this.mqttService.payloadToText(payload);
          if (!payloadText) {
            window.clearTimeout(responseTimer);
            reject(this.createNoDataFetchedError('Received an empty patient import response.'));
            return;
          }

          const response = this.mqttService.decodeJsonText<LeilaPatientImportResponse>(payloadText);
          if (!response || response.type !== 'patient_import_result' || response.requestId !== requestId) {
            window.clearTimeout(responseTimer);
            reject(this.createNoDataFetchedError('Received an invalid patient import response.'));
            return;
          }

          window.clearTimeout(responseTimer);

          if (!response.ok) {
            reject(this.mapImportErrorResponse(response));
            return;
          }

          resolve(this.mapImportedPatient(response.patient));
        };

        activeMqttClient.on('message', onMessageHandler);
        this.mqttService.publishJson(activeMqttClient, IMPORT_REQUEST_TOPIC, requestPayload, 1);
      });
    } catch (error) {
      if (error instanceof ImportPatientError) {
        throw error;
      }

      throw this.createNoDataFetchedError(error instanceof Error ? error.message : 'Patient import failed.');
    } finally {
      if (mqttClient && onMessageHandler) {
        mqttClient.off('message', onMessageHandler);
      }

      if (mqttClient) {
        this.mqttService.unsubscribeFromTopics(mqttClient, [responseTopic]);
      }

      this.mqttService.mqttDisconnect();
    }
  }

  private async connectWithTimeout(): Promise<MqttClient> {
    let connectTimer: number | null = null;

    try {
      return await Promise.race<MqttClient>([
        this.mqttService.mqttConnect('im-client'),
        new Promise<never>((_, reject) => {
          connectTimer = window.setTimeout(
            () => reject(this.createNoDataFetchedError('Timed out while connecting to LEILA.')),
            IMPORT_PATIENT_CONNECT_TIMEOUT_MS
          );
        }),
      ]);
    } finally {
      if (connectTimer !== null) {
        window.clearTimeout(connectTimer);
      }
    }
  }

  private mapImportedPatient(patient: LeilaImportedPatientPayload): IImportPatientData {
    return {
      external_id: patient.external_id ?? null,
      title: this.mapTitle(patient.title),
      last_name: patient.last_name,
      middle_name: patient.middle_name ?? null,
      first_name: patient.first_name ?? null,
      date_of_birth: patient.date_of_birth,
      street: patient.street ?? null,
      house_number: patient.house_number ?? null,
      city: patient.city ?? null,
      postal_code: patient.postal_code ?? null,
      country: this.resolveCountry(patient.country, patient.postal_code),
      phone: patient.phone ?? null,
      mobile: patient.mobile ?? null,
      email: patient.email ?? null,
    };
  }

  private mapTitle(title: LeilaImportedPatientPayload['title']): EPatientTitle | null {
    return title && Object.values(EPatientTitle).includes(title) ? title : null;
  }

  private resolveCountry(country: string | null | undefined, postalCode: string | null | undefined): string | null {
    const mappedCountry = this.mapCountryCode(country);
    if (mappedCountry) {
      return mappedCountry;
    }

    return this.getCountryFromPostalCode(postalCode);
  }

  private mapCountryCode(country: string | null | undefined): string | null {
    if (!country) {
      return null;
    }

    const code = country.trim().toUpperCase();
    return PODONET_COUNTRY_CODE_ALIASES[code] ?? code;
  }

  private getCountryFromPostalCode(postalCode: string | null | undefined): string | null {
    if (!postalCode) {
      return null;
    }

    const normalizedPostalCode = postalCode.trim();
    const match = POSTAL_CODE_COUNTRY_PATTERNS.find((entry) => entry.pattern.test(normalizedPostalCode));
    return match?.countryCode ?? null;
  }

  private mapImportErrorResponse(response: LeilaPatientImportErrorResponse): ImportPatientError {
    const code = response.code;

    if (
      code === EImportErrorCode.FileNotFound ||
      code === EImportErrorCode.InvalidFileType ||
      code === EImportErrorCode.InvalidFileContent
    ) {
      return new ImportPatientError(response.message ?? 'Patient import failed.', code);
    }

    return this.createNoDataFetchedError(response.message ?? 'No patient data could be fetched.');
  }

  private createNoDataFetchedError(message: string): ImportPatientError {
    return new ImportPatientError(message, EImportErrorCode.NoDataFetched);
  }
}
